import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { ApiError, errorMessage } from "../../shared/api/errors.ts";
import type { components } from "../../shared/api/schema";
import { devicesQueryKey, setActiveDeviceId } from "../../shared/devices/devices.ts";
import { useApiClient } from "../../shared/session/SessionProvider.tsx";
import { ArtEva } from "../../shared/ui/ArtEva/ArtEva.tsx";
import { Button, ButtonLink } from "../../shared/ui/Button/Button.tsx";
import styles from "../claim/ClaimPage.module.css";

type InvitePreview = components["schemas"]["InvitePreviewResponse"];

/**
 * Invite-ссылка от владельца (`/invite/<token>`): кто и на какую E-va приглашает, «Принять приглашение / Отклонить».
 * Отдельного макета нет, каркас — как у подтверждения привязки (06.02 / DEVICE, Figma 953:12871).
 */
export function InvitePage() {
  const token = useParams().token ?? "";
  const client = useApiClient();
  const preview = useQuery({
    queryKey: ["invite", token],
    queryFn: async () => {
      const { data, error, response } = await client.GET("/v1/invites/{token}", { params: { path: { token } } });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
    // Отозванный, истёкший или исчерпанный invite повтор не исправит.
    retry: false,
  });
  return (
    <main className={styles.page}>
      <h1 className={styles.title}>Приглашение</h1>
      <div className={styles.body}>
        <ArtEva width={212} className={styles.art} />
        {preview.data && <InviteForm token={token} preview={preview.data} />}
        {preview.error && <InvalidInvite error={preview.error} />}
      </div>
    </main>
  );
}

function InviteForm({ token, preview }: { token: string; preview: InvitePreview }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await client.POST("/v1/invites/{token}/accept", {
        params: { path: { token } },
      });
      if (!data) throw ApiError.from(response, error);
      return data.device;
    },
    onSuccess: async (device) => {
      setActiveDeviceId(device.id);
      await queryClient.invalidateQueries({ queryKey: devicesQueryKey });
      // replace: «назад» не должен возвращать на уже принятое приглашение.
      await navigate("/feed", { replace: true });
    },
  });

  return (
    <div className={styles.form}>
      <div className={styles.text}>
        <h2 className={styles.heading}>{invitation(preview)}</h2>
        <p className={styles.description}>Вы станете гостем этой E-va и сразу сможете отправлять на неё сообщения.</p>
      </div>
      <div className={styles.actions}>
        <Button type="button" loading={mutation.isPending} onClick={() => mutation.mutate()}>
          Принять приглашение
        </Button>
        {mutation.error && (
          <p className={styles.formError} role="alert">
            {errorMessage(mutation.error)}
          </p>
        )}
        <ButtonLink to="/feed" replace variant="secondary">
          Отклонить
        </ButtonLink>
      </div>
    </div>
  );
}

/** «Мария приглашает вас отправлять сообщения на E-va «Кухня»»; без имён — нейтральная форма. */
function invitation({ inviterName, deviceName }: InvitePreview): string {
  const inviter = inviterName?.trim();
  const name = deviceName?.trim();
  const target = name ? `E-va «${name}»` : "E-va";
  return inviter
    ? `${inviter} приглашает вас отправлять сообщения на ${target}`
    : `Вас приглашают отправлять сообщения на ${target}`;
}

function InvalidInvite({ error }: { error: Error }) {
  const isInviteError = error instanceof ApiError && error.code.startsWith("invite_");
  return (
    <div className={styles.form}>
      <div className={styles.text}>
        <h2 className={styles.heading}>
          {isInviteError ? "Приглашение недействительно" : "Не удалось открыть приглашение"}
        </h2>
        <p className={styles.description}>{errorMessage(error)}</p>
      </div>
      <div className={styles.actions}>
        <ButtonLink to="/feed" replace>
          На главную
        </ButtonLink>
      </div>
    </div>
  );
}
